/**
 * Pure File → base64 encoding helpers for LLM attachment payloads.
 *
 * Relocated downward from `features/chat/utils/chat-attachments` (ADR 024,
 * AssistantMarkdown precedent) so both the chat composer and the Studio
 * pipeline (`modules/pages/studio/pipeline/source-material`) can share them
 * without a features↔modules bidirectional import pair. `chat-attachments`
 * re-exports these for its existing consumers.
 */

/** Convert a File to base64 string (without data URL prefix) */
export async function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      const base64 = dataUrl.split(',')[1] ?? '';
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/**
 * Compress and resize an image for vision API submission.
 * Resizes to max 1568px on the longest side and compresses at 85% JPEG quality.
 * This keeps vision payloads reasonable for large phone photos.
 */
export async function compressImageForVision(file: File): Promise<{ data: string; mediaType: string }> {
  const MAX_DIM = 1568;
  const QUALITY = 0.85;

  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      let { width, height } = img;
      if (width > MAX_DIM || height > MAX_DIM) {
        if (width >= height) {
          height = Math.round((height / width) * MAX_DIM);
          width = MAX_DIM;
        } else {
          width = Math.round((width / height) * MAX_DIM);
          height = MAX_DIM;
        }
      }
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) { reject(new Error('Canvas not available')); return; }
      ctx.drawImage(img, 0, 0, width, height);
      const dataUrl = canvas.toDataURL('image/jpeg', QUALITY);
      const base64 = dataUrl.split(',')[1] ?? '';
      resolve({ data: base64, mediaType: 'image/jpeg' });
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Failed to load image')); };
    img.src = url;
  });
}
